import type { Channel } from '@/api/endpoints/channels'
import type { ChannelConfigOut, ChannelScheduleStateOut, LlmProviderMode } from '@/api/endpoints/scheduler'
import { channelColor } from '@/lib/channelColor'

/**
 * Os cards de canal do Scheduler: o modelo de cada card a partir das fontes da
 * página (estado do agendamento, config, cadastro do canal, rascunhos e fila de
 * capas) e a busca, os filtros e a ordem da grade. Tudo puro, para testar sem
 * DOM; o componente só desenha.
 */

/** ``partial``: parte dos vídeos da última execução falhou. */
export type ChannelHealth = 'ok' | 'running' | 'failed' | 'partial' | 'idle' | 'off'
export type ChannelFilter = 'all' | 'active' | 'attention'
export type ChannelSort = 'name' | 'next'
export type ChannelPlatform = 'youtube' | 'tiktok' | 'instagram'

export interface ChannelCardModel {
  slug: string
  name: string
  initials: string
  avatarUrl: string | null
  /** A plataforma principal primeiro, depois os destinos ligados. */
  platforms: ChannelPlatform[]
  factory: string
  factoryLabel: string
  /** Cor da factory: chip, iniciais e acentos do card. */
  accent: string
  enabled: boolean
  videosPerDay: number
  slots: string[]
  nextRunAt: string | null
  generating: boolean
  uploading: boolean
  health: ChannelHealth
  /** A última execução que falhou, inteira ou em parte; ``null`` quando nenhuma. */
  failure: ChannelFailure | null
  /** "Modelo de IA" do canal; o card só fala dele quando é o Ollama local. */
  llmProvider: LlmProviderMode
  drafts: number
  pendingCovers: number
  /** Pool de onde o canal tira os vídeos; null quando gera sozinho. */
  poolSlug: string | null
  poolName: string | null
  /** Próximo upload; num canal com pool é o que o card mostra no lugar da geração. */
  nextUploadAt: string | null
}

export interface ChannelFailure {
  /** "Geração falhou · hoje 02:14", "Upload parcial · ontem 08:01". */
  text: string
  /** Falhou em parte (alguns vídeos saíram). */
  partial: boolean
}

export interface ChannelView {
  query: string
  filter: ChannelFilter
  /** Slug da factory; vazio = todas. */
  factory: string
  sort: ChannelSort
}

export const DEFAULT_VIEW: ChannelView = { query: '', filter: 'all', factory: '', sort: 'name' }

// As factories que o scheduler despacha (``generation_runner._ADAPTERS``). Uma
// factory nova sem token cai numa cor estável da paleta de canais.
const FACTORY_ACCENTS: Readonly<Record<string, string>> = {
  viral_shorts: 'var(--factory-viral)',
  atlas: 'var(--factory-atlas)',
  quiz: 'var(--factory-quiz)',
  reddit_stories: 'var(--factory-reddit)',
  etymology: 'var(--factory-etymology)',
  ranked: 'var(--factory-ranked)',
  code_levels: 'var(--factory-code-levels)',
  pov_lives: 'var(--factory-long)',
  cortes: 'var(--factory-cortes)',
  football: 'var(--factory-football)',
  nasa_apod: 'var(--factory-apod)',
}

export function factoryAccent(factory: string): string {
  return FACTORY_ACCENTS[factory] ?? channelColor(factory)
}

// Conectivos que não entram nas iniciais: "Raiz das Palavras" vira RP, não RD.
const STOPWORDS = new Set(['a', 'o', 'e', 'de', 'da', 'do', 'das', 'dos', 'em', 'na', 'no', 'pro', 'pra', 'the', 'of'])

export function initials(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((w) => /[\p{L}\p{N}]/u.test(w))
  const strong = words.filter((w) => !STOPWORDS.has(w.toLocaleLowerCase('pt-BR')))
  const picked = (strong.length > 0 ? strong : words).slice(0, 2)
  const letters = picked.map((w) => /[\p{L}\p{N}]/u.exec(w)?.[0] ?? '')
  return letters.join('').toLocaleUpperCase('pt-BR') || '?'
}

const WEEKDAYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'] as const
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

function dayNumber(d: Date): number {
  return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86_400_000
}

/** "hoje", "amanhã", "ontem", o dia da semana até 6 dias à frente, senão "dd/mm". */
export function dayLabel(d: Date, now: Date): string {
  const diff = dayNumber(d) - dayNumber(now)
  if (diff === 0) return 'hoje'
  if (diff === 1) return 'amanhã'
  if (diff === -1) return 'ontem'
  if (diff > 1 && diff < 7) return WEEKDAYS[d.getDay()] ?? ''
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`
}

/** "hoje 21:00", "amanhã 02:00", "sáb 12:00"; uma data sem hora sai só com o dia. */
export function formatWhen(value: string | null | undefined, now: Date): string {
  if (!value) return '—'
  const dateOnly = DATE_ONLY.exec(value)
  if (dateOnly) {
    return dayLabel(new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3])), now)
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return `${dayLabel(d, now)} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function channelHealth(st: ChannelScheduleStateOut): ChannelHealth {
  if (!st.enabled) return 'off'
  const gen = st.last_run_status
  const upl = st.upload_last_run_status
  if (gen === 'running' || upl === 'running') return 'running'
  if (gen === 'failed' || upl === 'failed') return 'failed'
  if (gen === 'partial' || upl === 'partial') return 'partial'
  if (!gen && !upl) return 'idle'
  return 'ok'
}

/**
 * A falha que o card mostra. Independe de ``channelHealth``: com a geração
 * rodando e o upload anterior falho, o ponto fica amarelo e a falha continua
 * escrita (e contando como pendência).
 */
export function channelFailure(st: ChannelScheduleStateOut, now: Date): ChannelFailure | null {
  if (!st.enabled) return null
  const gen = formatWhen(st.ended_at || st.last_run_date, now)
  const upl = formatWhen(st.upload_ended_at || st.upload_last_run_date, now)
  if (st.last_run_status === 'failed') return { text: `Geração falhou · ${gen}`, partial: false }
  if (st.upload_last_run_status === 'failed') return { text: `Upload falhou · ${upl}`, partial: false }
  if (st.last_run_status === 'partial') return { text: `Geração parcial · ${gen}`, partial: true }
  if (st.upload_last_run_status === 'partial') return { text: `Upload parcial · ${upl}`, partial: true }
  return null
}

function isPlatform(p: string): p is ChannelPlatform {
  return p === 'youtube' || p === 'tiktok' || p === 'instagram'
}

export interface ChannelCardSources {
  states: Readonly<Record<string, ChannelScheduleStateOut>>
  configs: Readonly<Record<string, ChannelConfigOut>>
  channels: readonly Channel[]
  factoryLabels: ReadonlyMap<string, string>
  drafts: ReadonlyMap<string, number>
  pendingCovers: ReadonlyMap<string, number>
  /** Nome de cada pool, para o link no card do canal ligado. */
  poolNames?: ReadonlyMap<string, string>
  now: Date
}

export function buildChannelCards(src: ChannelCardSources): ChannelCardModel[] {
  const registry = new Map(src.channels.map((c) => [c.slug, c] as const))
  return Object.entries(src.states).map(([slug, st]) => {
    const channel = registry.get(slug)
    // Idem para pool_slug (fatia 5 dos pools).
    const poolSlug = st.pool_slug ?? null
    // Canal ligado a pool publica o que o pool gera: a factory dele é a do pool,
    // não a que ficou na agenda do canal (o default, se ele nunca gerou).
    const factory = (poolSlug ? src.configs[poolSlug]?.factory : undefined) ?? src.configs[slug]?.factory ?? ''
    const name = channel?.name ?? slug
    const primary: ChannelPlatform = channel?.platform ?? 'youtube'
    // O Pages publica o front no merge, antes do pull do backend: o backend
    // antigo ainda não manda ``destinations`` nem ``avatar_url``.
    const destinations = (st.destinations as string[] | undefined) ?? []
    return {
      slug,
      name,
      initials: initials(name),
      avatarUrl: channel?.avatar_url ?? null,
      platforms: [primary, ...destinations.filter(isPlatform).filter((p) => p !== primary)],
      factory,
      factoryLabel: src.factoryLabels.get(factory) ?? factory,
      accent: factoryAccent(factory),
      enabled: st.enabled,
      videosPerDay: st.videos_per_day,
      slots: st.publish_slots,
      nextRunAt: st.next_run_at,
      generating: st.enabled && st.last_run_status === 'running',
      uploading: st.enabled && st.upload_last_run_status === 'running',
      health: channelHealth(st),
      failure: channelFailure(st, src.now),
      llmProvider: src.configs[slug]?.llm_provider ?? 'auto',
      drafts: src.drafts.get(slug) ?? 0,
      pendingCovers: src.pendingCovers.get(slug) ?? 0,
      poolSlug,
      poolName: poolSlug ? (src.poolNames?.get(poolSlug) ?? poolSlug) : null,
      nextUploadAt: st.upload_next_run_at ?? null,
    }
  })
}

/** Pede atenção: falhou (inteira ou em parte), tem rascunho para aprovar ou capa presa na fila. */
export function needsAttention(c: ChannelCardModel): boolean {
  return c.enabled && (c.failure !== null || c.drafts > 0 || c.pendingCovers > 0)
}

export function normalize(text: string): string {
  return text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLocaleLowerCase('pt-BR')
}

function nextKey(c: ChannelCardModel): number {
  if (c.generating) return Number.NEGATIVE_INFINITY
  // Canal com pool não gera: a ordem "próxima" usa a próxima publicação.
  const when = c.poolSlug ? c.nextUploadAt : c.nextRunAt
  const t = when ? Date.parse(when) : Number.NaN
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t
}

function compareCards(a: ChannelCardModel, b: ChannelCardModel, sort: ChannelSort): number {
  if (a.enabled !== b.enabled) return a.enabled ? -1 : 1
  if (sort === 'next') {
    const ka = nextKey(a)
    const kb = nextKey(b)
    if (ka !== kb) return ka < kb ? -1 : 1
  }
  return a.name.localeCompare(b.name, 'pt-BR')
}

/** Busca, filtros e ordem. Canal desativado vai sempre para o fim. */
export function visibleCards(cards: readonly ChannelCardModel[], view: ChannelView): ChannelCardModel[] {
  const q = normalize(view.query.trim())
  return cards
    .filter((c) => {
      if (q && ![c.name, c.slug, c.factoryLabel].some((t) => normalize(t).includes(q))) return false
      if (view.filter === 'active' && !c.enabled) return false
      if (view.filter === 'attention' && !needsAttention(c)) return false
      if (view.factory && c.factory !== view.factory) return false
      return true
    })
    .sort((a, b) => compareCards(a, b, view.sort))
}

export function filterCounts(cards: readonly ChannelCardModel[]): Record<ChannelFilter, number> {
  return {
    all: cards.length,
    active: cards.filter((c) => c.enabled).length,
    attention: cards.filter(needsAttention).length,
  }
}

/** As factories presentes na grade, para o filtro, em ordem de rótulo. */
export function factoryOptions(cards: readonly ChannelCardModel[]): { value: string; label: string }[] {
  const seen = new Map<string, string>()
  for (const c of cards) {
    if (c.factory && !seen.has(c.factory)) seen.set(c.factory, c.factoryLabel)
  }
  return [...seen.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'))
}
